import React, { useState, useMemo } from 'react';
import { 
  Check, 
  X, 
  ChevronDown, 
  ChevronRight,
  FileCode,
  Plus,
  Minus,
  Copy,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

/**
 * Simple diff algorithm - returns array of change objects
 */
function computeDiff(oldText, newText) {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  
  const diff = [];
  let oldIdx = 0;
  let newIdx = 0;
  
  // Simple line-by-line diff
  while (oldIdx < oldLines.length || newIdx < newLines.length) {
    const oldLine = oldLines[oldIdx];
    const newLine = newLines[newIdx];
    
    if (oldIdx >= oldLines.length) {
      // New lines added at end
      diff.push({ type: 'add', content: newLine, newLineNum: newIdx + 1 });
      newIdx++;
    } else if (newIdx >= newLines.length) {
      // Old lines removed at end
      diff.push({ type: 'remove', content: oldLine, oldLineNum: oldIdx + 1 });
      oldIdx++;
    } else if (oldLine === newLine) {
      // Same line
      diff.push({ type: 'same', content: oldLine, oldLineNum: oldIdx + 1, newLineNum: newIdx + 1 });
      oldIdx++;
      newIdx++;
    } else {
      // Different - look ahead to find matches
      let foundMatch = false;
      
      // Look for old line in upcoming new lines (was moved down)
      for (let j = newIdx + 1; j < Math.min(newIdx + 5, newLines.length); j++) {
        if (newLines[j] === oldLine) {
          // Add intervening new lines
          for (let k = newIdx; k < j; k++) {
            diff.push({ type: 'add', content: newLines[k], newLineNum: k + 1 });
          }
          newIdx = j;
          foundMatch = true;
          break;
        }
      }
      
      // Look for new line in upcoming old lines (was moved up)
      if (!foundMatch) {
        for (let j = oldIdx + 1; j < Math.min(oldIdx + 5, oldLines.length); j++) {
          if (oldLines[j] === newLine) {
            // Remove intervening old lines
            for (let k = oldIdx; k < j; k++) {
              diff.push({ type: 'remove', content: oldLines[k], oldLineNum: k + 1 });
            }
            oldIdx = j;
            foundMatch = true;
            break;
          }
        }
      }
      
      if (!foundMatch) {
        // Simple modification - remove old, add new
        diff.push({ type: 'remove', content: oldLine, oldLineNum: oldIdx + 1 });
        diff.push({ type: 'add', content: newLine, newLineNum: newIdx + 1 });
        oldIdx++;
        newIdx++;
      }
    }
  }
  
  return diff;
}

/**
 * Get syntax highlighting class based on file extension
 */
function getLanguage(path) {
  const ext = path.split('.').pop()?.toLowerCase();
  const langMap = {
    tsx: 'typescript',
    ts: 'typescript',
    jsx: 'javascript',
    js: 'javascript',
    css: 'css',
    scss: 'scss',
    json: 'json',
    md: 'markdown',
    html: 'html',
  };
  return langMap[ext] || 'text';
}

/**
 * DiffViewer - Shows a side-by-side or unified diff view
 */
export function DiffViewer({ 
  change, 
  onAccept, 
  onReject, 
  expanded = false,
  isBrandTheme = false,
  isDark = false,
}) {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [viewMode, setViewMode] = useState('unified'); // 'unified' | 'split'
  
  const diff = useMemo(() => {
    return computeDiff(change.oldContent || '', change.newContent || '');
  }, [change.oldContent, change.newContent]);
  
  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    diff.forEach(d => {
      if (d.type === 'add') added++;
      if (d.type === 'remove') removed++;
    });
    return { added, removed };
  }, [diff]);
  
  const isAccepted = change.status === 'accepted';
  const isRejected = change.status === 'rejected';
  const isPending = change.status === 'pending';
  
  // Theme-aware colors
  const bgColor = isBrandTheme 
    ? 'bg-white/5 border-white/10' 
    : isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200';
  
  const headerBg = isBrandTheme 
    ? 'bg-white/5' 
    : isDark ? 'bg-gray-900/50' : 'bg-gray-50';
    
  const textPrimary = isBrandTheme 
    ? 'text-white' 
    : isDark ? 'text-white' : 'text-gray-900';
    
  const textSecondary = isBrandTheme 
    ? 'text-white/60' 
    : isDark ? 'text-gray-400' : 'text-gray-500';
    
  const textMuted = isBrandTheme 
    ? 'text-white/40' 
    : isDark ? 'text-gray-500' : 'text-gray-400';

  const addedBg = isBrandTheme
    ? 'bg-green-500/20'
    : isDark ? 'bg-green-900/30' : 'bg-green-50';
    
  const removedBg = isBrandTheme
    ? 'bg-red-500/20'
    : isDark ? 'bg-red-900/30' : 'bg-red-50';

  const addedText = isBrandTheme
    ? 'text-green-300'
    : isDark ? 'text-green-400' : 'text-green-700';
    
  const removedText = isBrandTheme
    ? 'text-red-300'
    : isDark ? 'text-red-400' : 'text-red-700';
    
  const lineNumColor = isBrandTheme
    ? 'text-white/30'
    : isDark ? 'text-gray-600' : 'text-gray-400';

  return (
    <Card className={cn('overflow-hidden border', bgColor)}>
      {/* Header */}
      <div 
        className={cn(
          'px-3 py-2 flex items-center gap-2 cursor-pointer',
          headerBg,
          'hover:bg-opacity-80 transition-colors'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className={cn('w-4 h-4', textMuted)} />
        ) : (
          <ChevronRight className={cn('w-4 h-4', textMuted)} />
        )}
        
        <FileCode className={cn('w-4 h-4', textSecondary)} />
        
        <span className={cn('text-sm font-mono flex-1 truncate', textPrimary)}>
          {change.path}
        </span>
        
        {/* Stats */}
        <div className="flex items-center gap-2 text-xs">
          {stats.added > 0 && (
            <span className={addedText}>+{stats.added}</span>
          )}
          {stats.removed > 0 && (
            <span className={removedText}>-{stats.removed}</span>
          )}
        </div>
        
        {/* Status badge */}
        {isAccepted && (
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Accepted
          </Badge>
        )}
        {isRejected && (
          <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        )}
        
        {/* Actions for pending changes */}
        {isPending && (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button
              size="sm"
              variant="ghost"
              onClick={onAccept}
              className="h-7 px-2 text-green-500 hover:text-green-400 hover:bg-green-500/10"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onReject}
              className="h-7 px-2 text-red-500 hover:text-red-400 hover:bg-red-500/10"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Diff Content */}
      {isExpanded && (
        <div className="border-t border-inherit">
          {/* View mode toggle */}
          <div className={cn('px-3 py-1 flex items-center gap-2 border-b border-inherit', headerBg)}>
            <button
              onClick={() => setViewMode('unified')}
              className={cn(
                'text-xs px-2 py-0.5 rounded',
                viewMode === 'unified' 
                  ? (isBrandTheme ? 'bg-white/10 text-white' : isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900')
                  : textMuted
              )}
            >
              Unified
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={cn(
                'text-xs px-2 py-0.5 rounded',
                viewMode === 'split'
                  ? (isBrandTheme ? 'bg-white/10 text-white' : isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900')
                  : textMuted
              )}
            >
              Split
            </button>
          </div>
          
          {/* Diff lines */}
          <div className="max-h-80 overflow-auto">
            {viewMode === 'unified' ? (
              <UnifiedDiff 
                diff={diff} 
                addedBg={addedBg}
                removedBg={removedBg}
                addedText={addedText}
                removedText={removedText}
                lineNumColor={lineNumColor}
                textPrimary={textPrimary}
              />
            ) : (
              <SplitDiff 
                diff={diff}
                addedBg={addedBg}
                removedBg={removedBg}
                addedText={addedText}
                removedText={removedText}
                lineNumColor={lineNumColor}
                textPrimary={textPrimary}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Unified diff view
 */
function UnifiedDiff({ diff, addedBg, removedBg, addedText, removedText, lineNumColor, textPrimary }) {
  return (
    <table className="w-full text-xs font-mono">
      <tbody>
        {diff.map((line, idx) => (
          <tr 
            key={idx}
            className={cn(
              line.type === 'add' && addedBg,
              line.type === 'remove' && removedBg
            )}
          >
            <td className={cn('w-10 px-2 py-0.5 text-right select-none', lineNumColor)}>
              {line.oldLineNum || ''}
            </td>
            <td className={cn('w-10 px-2 py-0.5 text-right select-none', lineNumColor)}>
              {line.newLineNum || ''}
            </td>
            <td className={cn('w-6 px-1 py-0.5 text-center select-none', 
              line.type === 'add' ? addedText : line.type === 'remove' ? removedText : lineNumColor
            )}>
              {line.type === 'add' ? '+' : line.type === 'remove' ? '-' : ' '}
            </td>
            <td className={cn(
              'px-2 py-0.5 whitespace-pre',
              line.type === 'add' ? addedText : line.type === 'remove' ? removedText : textPrimary
            )}>
              {line.content}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Split diff view
 */
function SplitDiff({ diff, addedBg, removedBg, addedText, removedText, lineNumColor, textPrimary }) {
  // Group diff into pairs for split view
  const pairs = [];
  let i = 0;
  
  while (i < diff.length) {
    const current = diff[i];
    
    if (current.type === 'same') {
      pairs.push({ left: current, right: current });
      i++;
    } else if (current.type === 'remove') {
      const next = diff[i + 1];
      if (next && next.type === 'add') {
        pairs.push({ left: current, right: next });
        i += 2;
      } else {
        pairs.push({ left: current, right: null });
        i++;
      }
    } else if (current.type === 'add') {
      pairs.push({ left: null, right: current });
      i++;
    } else {
      i++;
    }
  }
  
  return (
    <div className="flex">
      {/* Left side - old */}
      <div className="flex-1 border-r border-inherit">
        <table className="w-full text-xs font-mono">
          <tbody>
            {pairs.map((pair, idx) => (
              <tr 
                key={idx}
                className={cn(pair.left?.type === 'remove' && removedBg)}
              >
                <td className={cn('w-10 px-2 py-0.5 text-right select-none', lineNumColor)}>
                  {pair.left?.oldLineNum || ''}
                </td>
                <td className={cn(
                  'px-2 py-0.5 whitespace-pre',
                  pair.left?.type === 'remove' ? removedText : textPrimary
                )}>
                  {pair.left?.content || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Right side - new */}
      <div className="flex-1">
        <table className="w-full text-xs font-mono">
          <tbody>
            {pairs.map((pair, idx) => (
              <tr 
                key={idx}
                className={cn(pair.right?.type === 'add' && addedBg)}
              >
                <td className={cn('w-10 px-2 py-0.5 text-right select-none', lineNumColor)}>
                  {pair.right?.newLineNum || ''}
                </td>
                <td className={cn(
                  'px-2 py-0.5 whitespace-pre',
                  pair.right?.type === 'add' ? addedText : textPrimary
                )}>
                  {pair.right?.content || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * PendingChangesPanel - Shows all pending changes with accept/reject all
 */
export function PendingChangesPanel({
  changes,
  onAcceptChange,
  onRejectChange,
  onAcceptAll,
  onRejectAll,
  isBrandTheme = false,
  isDark = false,
}) {
  const pendingChanges = changes.filter(c => c.status === 'pending');
  const acceptedChanges = changes.filter(c => c.status === 'accepted');
  const rejectedChanges = changes.filter(c => c.status === 'rejected');
  
  const textPrimary = isBrandTheme 
    ? 'text-white' 
    : isDark ? 'text-white' : 'text-gray-900';
    
  const textSecondary = isBrandTheme 
    ? 'text-white/60' 
    : isDark ? 'text-gray-400' : 'text-gray-500';
    
  const surfaceBg = isBrandTheme 
    ? 'bg-white/5' 
    : isDark ? 'bg-gray-800/50' : 'bg-gray-50';
    
  const borderColor = isBrandTheme 
    ? 'border-white/10' 
    : isDark ? 'border-gray-700' : 'border-gray-200';

  if (changes.length === 0) {
    return (
      <div className={cn('p-4 text-center', textSecondary)}>
        <FileCode className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No changes proposed yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with actions */}
      {pendingChanges.length > 0 && (
        <div className={cn('flex items-center justify-between p-2 rounded-lg', surfaceBg)}>
          <span className={cn('text-sm', textSecondary)}>
            {pendingChanges.length} pending change{pendingChanges.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={onAcceptAll}
              className="h-7 text-xs text-green-500 hover:text-green-400 hover:bg-green-500/10"
            >
              <Check className="w-3 h-3 mr-1" />
              Accept All
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRejectAll}
              className="h-7 text-xs text-red-500 hover:text-red-400 hover:bg-red-500/10"
            >
              <X className="w-3 h-3 mr-1" />
              Reject All
            </Button>
          </div>
        </div>
      )}
      
      {/* Change list */}
      <div className="space-y-2">
        {changes.map((change) => (
          <DiffViewer
            key={change.changeId}
            change={change}
            onAccept={() => onAcceptChange(change.changeId)}
            onReject={() => onRejectChange(change.changeId)}
            expanded={change.status === 'pending'}
            isBrandTheme={isBrandTheme}
            isDark={isDark}
          />
        ))}
      </div>
      
      {/* Summary */}
      {(acceptedChanges.length > 0 || rejectedChanges.length > 0) && (
        <div className={cn('flex items-center gap-4 p-2 text-xs', textMuted)}>
          {acceptedChanges.length > 0 && (
            <span className="text-green-500">
              {acceptedChanges.length} accepted
            </span>
          )}
          {rejectedChanges.length > 0 && (
            <span className="text-red-500">
              {rejectedChanges.length} rejected
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default DiffViewer;



